/**
 * Live-Verbindung zum LadenHub: WebSocket mit Wiederverbindung; fällt die Verbindung aus, pollt die Kasse alle 3 s.
 * Nachrichten lösen eine Aktualisierung des Warenkorbs aus.
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { HubNachricht } from "../../worker/durable/LadenHub";
import { KEY_WARENKORB } from "./api";

export function useLiveVerbindung(aktiv: boolean, onNachricht?: (n: HubNachricht) => void) {
  const qc = useQueryClient();
  const [verbunden, setVerbunden] = useState(false);
  const cb = useRef(onNachricht);
  cb.current = onNachricht;

  useEffect(() => {
    if (!aktiv) return;
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let beendet = false;
    let versuch = 0;

    const pollStart = () => {
      if (poll) return;
      poll = setInterval(() => void qc.invalidateQueries({ queryKey: KEY_WARENKORB }), 3000);
    };
    const pollStop = () => {
      if (poll) clearInterval(poll);
      poll = null;
    };

    const verbinde = () => {
      if (beendet) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${location.host}/api/kasse/ws`);
      ws.onopen = () => {
        versuch = 0;
        setVerbunden(true);
        pollStop();
      };
      ws.onmessage = (ev) => {
        try {
          const n = JSON.parse(String(ev.data)) as HubNachricht;
          if (n.art === "warenkorb" || n.art === "sumup") void qc.invalidateQueries({ queryKey: ["kasse"] });
          cb.current?.(n);
        } catch {
          // ignorieren
        }
      };
      ws.onclose = () => {
        setVerbunden(false);
        if (beendet) return;
        pollStart();
        versuch++;
        timer = setTimeout(verbinde, Math.min(10_000, 1000 * 2 ** Math.min(versuch, 4)));
      };
      ws.onerror = () => ws?.close();
    };
    verbinde();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25_000);
    return () => {
      beendet = true;
      clearInterval(ping);
      if (timer) clearTimeout(timer);
      pollStop();
      ws?.close();
    };
  }, [aktiv, qc]);

  return verbunden;
}
